import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqCategories = [
    {
      category: "Menu & Ingredients",
      icon: "ChefHat",
      color: "primary",
      faqs: [
        {
          question: "Do you offer vegetarian and vegan options?",
          answer: `Absolutely! We have an extensive selection of vegetarian and vegan dishes that celebrate the plant-based traditions of Mediterranean cuisine. Our menu includes:

• Vegetarian kebabs made with grilled vegetables and halloumi
• Vegan falafel and hummus platters
• Fresh Mediterranean salads with dairy-free options
• Stuffed grape leaves (dolmas) prepared without meat
• Roasted eggplant and zucchini specialties

All our vegetarian and vegan dishes are prepared in dedicated areas to prevent cross-contamination, and our staff is trained to guide you through the best options for your dietary preferences.`
        },
        {
          question: "What allergens are present in your dishes?",
          answer: `We take food allergies very seriously and maintain detailed allergen information for all our dishes. Common allergens in our kitchen include:

• Gluten (in pita bread, some marinades)
• Dairy (in yogurt sauces, cheese)
• Nuts (in baklava, some rice dishes)
• Sesame (in tahini, hummus)
• Eggs (in some desserts)

We have gluten-free pita options and can modify most dishes to accommodate allergies. Please inform your server about any allergies when ordering, and we'll ensure your meal is prepared safely with dedicated utensils and preparation areas.`
        },
        {
          question: "Are your meats halal?",
          answer: `Yes, all our meats are certified halal and sourced from trusted suppliers who follow strict halal guidelines. We understand the importance of halal dining for our Muslim customers and ensure:

• All lamb, beef, and chicken are halal-certified
• No pork or alcohol is used in any of our dishes
• Separate preparation areas for halal items
• Staff training on halal food handling practices

Our halal certification is displayed in the restaurant, and we're happy to provide documentation upon request.`
        }
      ]
    },
    {
      category: "Dining & Reservations",
      icon: "Calendar",
      color: "accent",
      faqs: [
        {
          question: "Do I need a reservation?",
          answer: `Reservations are recommended but not required for most dining times. Here's what we suggest:

• Weekday lunch: Walk-ins usually accommodated
• Weekday dinner: Reservations recommended after 6 PM
• Weekend dining: Reservations strongly recommended
• Large groups (6+ people): Reservations required
• Special occasions: Book 2-3 days in advance

You can make reservations by calling us, using our online booking system, or through OpenTable. We hold tables for 15 minutes past reservation time, and we'll always try to accommodate walk-ins when possible.`
        },
        {
          question: "What\'s your seating capacity and group dining policy?",
          answer: `Our restaurant comfortably seats 120 guests across different dining areas:

• Main dining room: 80 seats with Mediterranean ambiance
• Private dining room: 25 seats for special events
• Outdoor patio: 15 seats (seasonal, weather permitting)

For groups of 8 or more, we require advance notice and may apply an 18% service charge. We offer special group menus and can accommodate dietary restrictions with advance planning. Private dining room rental is available for special occasions with customized menus.`
        },
        {
          question: "Is the restaurant family-friendly?",
          answer: `Absolutely! We welcome families and have created a warm, inclusive environment for guests of all ages:

• Children's menu with kid-friendly Mediterranean options
• High chairs and booster seats available
• Changing table in family restroom
• Coloring sheets and crayons for young diners
• Early bird family hours (5-7 PM) with quieter atmosphere
• Staff trained to accommodate families with children

We believe food brings families together, and we're honored to be part of your family dining experiences.`
        }
      ]
    },
    {
      category: "Ordering & Delivery",
      icon: "ShoppingBag",
      color: "conversion",
      faqs: [
        {
          question: "What are your delivery zones and fees?",
          answer: `We deliver within a 5-mile radius of our restaurant with the following structure:

• 0-2 miles: $2.99 delivery fee
• 2-3 miles: $3.99 delivery fee  
• 3-5 miles: $4.99 delivery fee
• Orders over $35: Free delivery within 3 miles

Delivery typically takes 25-35 minutes depending on distance and order complexity. We use insulated bags to ensure your food arrives hot and fresh. You can track your order in real-time through our app or website.`
        },
        {
          question: "Can I customize my order for dietary restrictions?",
          answer: `Yes! We're happy to customize most dishes to meet your dietary needs:

• Remove or substitute ingredients (extra charges may apply)
• Adjust spice levels from mild to extra hot
• Prepare dishes without certain allergens
• Modify cooking methods (grilled instead of fried)
• Provide sauces and dressings on the side

When ordering online, use the "Special Instructions" field, or call us directly for complex modifications. Our kitchen team is experienced in accommodating various dietary requirements while maintaining authentic flavors.`
        },
        {
          question: "What\'s your policy on order mistakes or issues?",
          answer: `Your satisfaction is our priority. If there's any issue with your order:

• Contact us immediately - we'll make it right• Missing items: We'll deliver them at no charge
• Incorrect orders: Full remake or refund
• Quality concerns: Replacement meal or credit
• Delivery issues: Refund of delivery fees

We stand behind our food 100%. Most issues are resolved with a quick phone call, and we often provide credits for future orders as an apology for any inconvenience.`
        }
      ]
    },
    {
      category: "Catering & Events",
      icon: "Users",
      color: "trust",
      faqs: [
        {
          question: "What catering packages do you offer?",
          answer: `We offer flexible catering options for events of all sizes:

• Mediterranean Mezze Package: Assorted appetizers and dips (10-50 people)
• Kebab Feast Package: Mixed grill platters with sides (15-100 people)  
• Executive Lunch Package: Individual portions for business meetings (5-30 people)
• Wedding/Celebration Package: Full-service with setup and service staff (50-200 people)

All packages include serving utensils, plates, and napkins. We can customize any package to meet dietary restrictions, budget requirements, or cultural preferences. Delivery and setup services available.`
        },
        {
          question: "How far in advance should I book catering?",
          answer: `Booking timeline depends on your event size and requirements:

• Small office lunches (under 20 people): 24-48 hours notice
• Medium events (20-50 people): 3-5 days advance booking
• Large events (50+ people): 1-2 weeks advance booking
• Wedding/major celebrations: 2-4 weeks advance booking

During busy seasons (holidays, graduation, wedding season), we recommend booking as early as possible. Rush orders may be accommodated with additional fees, subject to kitchen capacity and ingredient availability.`
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, faqIndex) => {
    const key = `${categoryIndex}-${faqIndex}`;
    setOpenFAQ(openFAQ === key ? null : key);
  };

  const getColorClasses = (color) => {
    const colorMap = {
      primary: "bg-primary-100 text-primary-700 border-primary-200",
      accent: "bg-accent-100 text-accent-700 border-accent-200",
      conversion: "bg-conversion-100 text-conversion-700 border-conversion-200",
      trust: "bg-trust-100 text-trust-700 border-trust-200"
    };
    return colorMap[color] || colorMap.primary;
  };

  return (
    <div className="space-y-12">
      {/* Header */}
      <div className="text-center">
        <h2 className="font-headline text-3xl font-bold text-text-primary mb-4">
          Frequently Asked Questions
        </h2>
        <p className="font-body text-lg text-text-secondary max-w-3xl mx-auto">
          Find quick answers to common questions about our menu, dining experience, and services. 
          Can't find what you're looking for? Don't hesitate to contact us directly.
        </p>
      </div>

      {/* FAQ Categories */}
      <div className="space-y-8">
        {faqCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} className="bg-surface rounded-xl shadow-warm overflow-hidden">
            {/* Category Header */}
            <div className={`p-6 border-b border-primary-100 ${getColorClasses(category.color)}`}>
              <h3 className="font-headline text-xl font-bold flex items-center">
                <Icon name={category.icon} size={24} className="mr-3" />
                {category.category}
              </h3>
            </div>

            {/* FAQ Items */}
            <div className="divide-y divide-primary-100">
              {category.faqs.map((faq, faqIndex) => {
                const isOpen = openFAQ === `${categoryIndex}-${faqIndex}`;
                return (
                  <div key={faqIndex} className="p-6">
                    <button
                      onClick={() => toggleFAQ(categoryIndex, faqIndex)}
                      className="w-full text-left flex items-center justify-between group"
                    >
                      <h4 className="font-cta font-semibold text-lg text-text-primary group-hover:text-primary transition-organic pr-4">
                        {faq.question}
                      </h4>
                      <Icon
                        name={isOpen ? "ChevronUp" : "ChevronDown"}
                        size={20}
                        className={`text-text-secondary group-hover:text-primary transition-organic flex-shrink-0 ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    
                    <div className={`transition-organic overflow-hidden ${
                      isOpen ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'
                    }`}>
                      <div className="prose prose-lg max-w-none text-text-secondary">
                        <p className="mb-4">{faq.answer}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Contact CTA */}
      <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-xl p-8 text-center">
        <h3 className="font-headline text-2xl font-bold text-text-primary mb-4">
          Still Have Questions?
        </h3>
        <p className="text-text-secondary mb-6 max-w-2xl mx-auto">
          Our friendly team is here to help! Whether you need menu recommendations, want to discuss 
          catering options, or have specific dietary concerns, we're just a call or message away.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="tel:+15551234567"
            className="btn-primary inline-flex items-center justify-center space-x-2"
          >
            <Icon name="Phone" size={20} />
            <span>Call Us Now</span>
          </a>
          <a
            href="mailto:hello@kebabpalace.com"
            className="btn-conversion inline-flex items-center justify-center space-x-2"
          >
            <Icon name="Mail" size={20} />
            <span>Send Email</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQSection;